import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTokens } from '../theme.jsx';

/**
 * Shared markdown renderer for Coffee Chats AI assistant replies (institution +
 * super-admin consoles). Uses GFM so tables, task lists, strikethrough, and fenced
 * code blocks render instead of leaking raw `| … |` or ``` markers.
 */
export default function AssistantMarkdown({ content }) {
  const t = useTokens();
  if (!content) { return null; }

  return (
    <div className="text-left text-[15px] leading-7" style={{ color: t.body, wordBreak: 'break-word' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: (props) => {
            return (
              <h1
                className="mb-3 mt-6 text-2xl font-semibold first:mt-0"
                style={{ color: t.heading, fontFamily: t.fonts.display }}
                {...props}
              />
            );
          },
          h2: (props) => {
            return (
              <h2
                className="mb-2 mt-5 text-xl font-semibold first:mt-0"
                style={{ color: t.heading, fontFamily: t.fonts.display }}
                {...props}
              />
            );
          },
          h3: (props) => {
            return (
              <h3
                className="mb-2 mt-4 text-base font-semibold first:mt-0"
                style={{ color: t.heading }}
                {...props}
              />
            );
          },
          p: (props) => { return <p className="mb-3 last:mb-0" {...props} />; },
          ul: (props) => { return <ul className="mb-3 list-disc space-y-1 pl-6 last:mb-0" {...props} />; },
          ol: (props) => { return <ol className="mb-3 list-decimal space-y-1 pl-6 last:mb-0" {...props} />; },
          li: (props) => { return <li className="leading-7" {...props} />; },
          strong: (props) => { return <strong className="font-semibold" style={{ color: t.heading }} {...props} />; },
          em: (props) => { return <em className="italic" {...props} />; },
          del: (props) => { return <del className="opacity-75" {...props} />; },
          hr: (props) => { return <hr className="my-4 border-0 border-t" style={{ borderColor: t.hairline }} {...props} />; },
          blockquote: (props) => {
            return (
              <blockquote
                className="my-3 border-l-2 pl-4 italic"
                style={{ borderColor: t.border, color: t.muted }}
                {...props}
              />
            );
          },
          a: ({ href, ...props }) => {
            return (
              <a
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                className="underline underline-offset-2"
                style={{ color: t.heading }}
                {...props}
              />
            );
          },
          code: ({ className, children, ...props }) => {
            if (className) {
              return (
                <code
                  className={`${className} whitespace-pre-wrap font-normal`}
                  style={{ fontFamily: t.fonts.mono }}
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <code
                className="rounded px-1.5 py-0.5 text-[13px]"
                style={{ backgroundColor: t.statBg, fontFamily: t.fonts.mono }}
                {...props}
              >
                {children}
              </code>
            );
          },
          pre: (props) => {
            return (
              <pre
                className="mb-3 overflow-x-auto rounded-xl p-4 text-[13px] leading-5 last:mb-0"
                style={{ backgroundColor: t.statBg, fontFamily: t.fonts.mono, color: t.body }}
                {...props}
              />
            );
          },
          table: (props) => {
            return (
              <div className="mb-3 overflow-x-auto last:mb-0">
                <table className="w-full border-collapse text-sm" {...props} />
              </div>
            );
          },
          thead: (props) => { return <thead {...props} />; },
          tbody: (props) => { return <tbody {...props} />; },
          tr: (props) => { return <tr {...props} />; },
          th: (props) => {
            return (
              <th
                className="border px-3 py-2 text-left font-semibold"
                style={{ borderColor: t.border, color: t.heading, backgroundColor: t.statBg }}
                {...props}
              />
            );
          },
          td: (props) => {
            return (
              <td
                className="border px-3 py-2 align-top"
                style={{ borderColor: t.hairline }}
                {...props}
              />
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
